import {Championships, db, Companies, Events, CompanyLogs, Tournaments, loadDb} from "../models/models.js";
import {
    getCompanyEvents,
    getCompanyChampionships,
    addCompanyCompanyLogs,
    getCompanyFacilities, getCompanyStaff, removeCompanyChampionships, getCompanyRosters
} from "../models/company.js";
import {
    addChildEvent, Event,
    getChildEvents,
    getEventMatchContracts, getEventSegments,
    getEventStreaks, getEventTournaments,
    getParentEvent
} from "../models/event.js";
import {
    checkAvailability,
    checkSuperstarBrandAvailability, getEmptySuperstarGraphic,
    getRosterMemberSuperstars,
    getSuperstarManagerUsedForMatchOrSegment
} from "./superstars.js";
import {
    getAvailableStaffMembersByRole,
    getCompanyLatestEvent, getCompanyMonth,
    getCompanyParentEvents,
    getCompanyRosterByType, getTournamentDisplayData, getTutorialStateName
} from "./companies.js";
import {getHypeForMatch, getMatchData, getMatchHype, getWinLossPercentagesWeights} from "./matches.js";
import {getEventCompany, getEventMatches} from "../models/event.js";
import {getRosterSuperstars, getRosterBrands} from "../models/roster.js";
import {
    getBrandColorForEvent,
    getBrandForEvent,
    getBrandIndexForWeeklyShow,
    removeObjectBrand,
    setObjectBrand
} from "./brands.js";
import {
    annualTournamentPostersData, captureError, commaSeparate, decimalRound,
    eventPostersData, eventPrestigeXps, generateName, getArrayMaxByProperty, getArrayMinByProperty,
    getCurrentGame,
    getFirstSunday,
    getRandomChoice, getRandomInt, getRosterMemberAlignment, getRosterMemberPopularity, getUniqueObjects,
    myround, objectInArray, weightedChoice
} from "./helpers.js";
import {getGameCurrentUserCompany, getGameMainUserCompany, saveGameDataToLocalStorage} from "./games.js";
import {getTournamentEvent, getTournamentMatches} from "../models/tournament.js";
import {globalUser} from "./globals.js";
import {
    getMatchChampionships,
    getMatchEvent,
    getMatchFeuds,
    getMatchGimmickMatch,
    getMatchParticipants, getMatchTagTeams
} from "../models/match.js";
import {formatCompanyNameBackend, getChampionshipDisplayName} from "../ui/ui-helpers.js";
import {getStreakEvent, getStreakTalent} from "../models/streak.js";
import {getMatchContractChampionship, getMatchContractEvent, getMatchContractTalent} from "../models/matchContract.js";
import {CompanyLog} from "../models/companyLog.js";
import {getSegmentData} from "./segments.js";
import {getSegmentParticipants, getSegmentTagTeams} from "../models/segment.js";
import {endEvent} from "./endEvent.js";
import {checkAreChampionshipsEqual, getChampionshipHolder} from "./championships.js";
import {matchFinishesData} from "../data/matchFinishes.js";
import {
    getAnnualTournamentMatchPredictions,
    getAnnualTournamentOpeningSegmentData,
    getAnnualTournamentPreviewTexts
} from "./annualTournament.js";
import {loadUser} from "./user.js";
import {getTagTeamSuperstars} from "../models/tagTeam.js";
import {checkToGoToNextTutorialState} from "./tutorial.js";

function getNextMonthlyEvent(monthlyEvent, parentEvents) {  // get_next_monthly_event
    // Get next monthly event from parent events
    let monthlyEventIndex = parentEvents.findIndex(x => x.$loki === monthlyEvent.$loki) + 1;
    if (monthlyEventIndex >= parentEvents.length) {
        monthlyEventIndex = 0
    }
    return parentEvents[monthlyEventIndex];
}

async function getMonthlyEventForWeeklyEvent(weeklyEvent, parentEvents, company) {  // get_monthly_event_for_weekly_show

    // Find parent monthly event
    let monthlyEvent = parentEvents.find(x => x.name === weeklyEvent.monthlyEventName);

    // If name changed, get latest monthly event and then its parent event
    if (!monthlyEvent) {
        let companyEvents = await getCompanyEvents(company);
        let latestMonthlyEvent = getLatestMonthlyEvent(companyEvents);
        monthlyEvent = await getParentEvent(latestMonthlyEvent);

        // Get monthly event after latest if weekly show before monthly or at monthly event
        if (weeklyEvent.weekNumber < 0 || weeklyEvent.weekNumber === null) {
            monthlyEvent = getNextMonthlyEvent(monthlyEvent, parentEvents)
        }
    }

    return monthlyEvent;
}

async function getUpcomingMonthlyEventForWeeklyEvent(weeklyEvent, parentEvents, company) {  // get_upcoming_monthly_event_for_weekly_show
    let upcomingMonthlyEvent = await getMonthlyEventForWeeklyEvent(weeklyEvent, parentEvents, company);
    // If weekly show after monthly event, get next monthly event
    if (weeklyEvent.weekNumber > 0) {
        upcomingMonthlyEvent = getNextMonthlyEvent(upcomingMonthlyEvent, parentEvents)
    }
    return upcomingMonthlyEvent;
}

function getLatestMonthlyEvent(events) {  // Previously get_latest_monthly_event
    // Sorts events by latest first
    let sortedEvents = events.slice().sort((a, b) => b.$loki - a.$loki);
    let latestMonthlyEvent;
    for (const x of sortedEvents) {
        if (!x.weeklyShow && x.name !== 'Weekly Show' && !x.temporaryEvent) {
            latestMonthlyEvent = x;
            break;
        }
    }
    return latestMonthlyEvent;
}

async function getNextEventFromCurrentEvent(currentEvent, parentEvents, company, numberOfWeeklyShows, weeklyShowName) {  // get_next_event_from_current_event
    let roster = await getCompanyRosterByType(company, 'main');
    let rosterBrands = await getRosterBrands(roster);
    let firstBrand = rosterBrands.length ? rosterBrands[0] : null;
    let nextEvent;

    // If currentEvent is weekly event
    if (currentEvent.weeklyShow && currentEvent.monthlyEventName) {
        let monthlyEvent = await getMonthlyEventForWeeklyEvent(currentEvent, parentEvents, company);
        let finalWeekNumber = Math.floor(numberOfWeeklyShows / 2);

        // If at brand's weekly event and next brand is present,
        // add weekly show for next brand with same attributes as current event
        let currentBrandIndex = getBrandIndexForWeeklyShow(currentEvent, rosterBrands);
        if (currentBrandIndex !== null && currentBrandIndex + 1 < rosterBrands.length) {
            let nextBrand = rosterBrands[currentBrandIndex + 1];
            nextEvent = await addWeeklyShow(weeklyShowName, currentEvent.monthlyEventName,
                currentEvent.weekNumber, nextBrand);
        }
        // Otherwise go to next event
        else {
            // Weekly show after monthly event
            if (currentEvent.weekNumber > 0) {
                // If this show is final week number, add weekly show before next monthly event
                if (currentEvent.weekNumber >= finalWeekNumber) {
                    monthlyEvent = getNextMonthlyEvent(monthlyEvent, parentEvents);
                    if (monthlyEvent.weeklyShowBeforeName) {
                        weeklyShowName = monthlyEvent.weeklyShowBeforeName;
                    }
                    // If 3 or more weekly shows per month, hold 2 shows before monthly event.
                    // If less than 3 weekly shows per month, hold 1 event before monthly event.
                    let weekNumber = numberOfWeeklyShows >= 3 ? -2 : -1;
                    nextEvent = await addWeeklyShow(weeklyShowName, monthlyEvent.name, weekNumber, firstBrand);
                }
                // Otherwise add next weekly show
                else {
                    if (monthlyEvent.weeklyShowAfterName) {
                        weeklyShowName = monthlyEvent.weeklyShowAfterName;
                    }
                    nextEvent = await addWeeklyShow(weeklyShowName, monthlyEvent.name, 2, firstBrand);
                }
            }
            // Weekly show before monthly event
            else {
                // If week right before monthly event, add upcoming monthly event
                if (currentEvent.weekNumber === -1) {
                    nextEvent = await addMonthlyEvent(monthlyEvent, parentEvents);
                }
                // Otherwise add next weekly show
                else {
                    if (monthlyEvent.weeklyShowBeforeName) {
                        weeklyShowName = monthlyEvent.weeklyShowBeforeName;
                    }
                    nextEvent = await addWeeklyShow(weeklyShowName, monthlyEvent.name, -1, firstBrand);
                }
            }
        }
    }
    // If currentEvent is monthly event
    else {
        let monthlyEvent = await getParentEventForEvent(parentEvents, currentEvent);

        // Add weekly show if running weekly shows
        if (numberOfWeeklyShows > 0) {
            let weekNumber;
            // If running one weekly show per month, add weekly show before next monthly event
            if (numberOfWeeklyShows === 1) {
                weekNumber = -1;
                monthlyEvent = getNextMonthlyEvent(monthlyEvent, parentEvents);
            }
            // Otherwise, add weekly show after this monthly event
            else {
                weekNumber = 1;
            }

            // Add weekly show
            if (monthlyEvent.weeklyShowAfterName) {
                weeklyShowName = monthlyEvent.weeklyShowAfterName;
            }
            nextEvent = await addWeeklyShow(weeklyShowName, monthlyEvent.name, weekNumber, firstBrand);
        }
        // Otherwise add next monthly event
        else {
            monthlyEvent = getNextMonthlyEvent(monthlyEvent, parentEvents);
            nextEvent = await addMonthlyEvent(monthlyEvent, parentEvents);
        }
    }

    return nextEvent;
}

async function getNextEvent(currentEvent, parentEvents, company, numberOfWeeklyShows = 2) {  // get_next_event
    // Get weekly show name
    let weeklyShowName = company.weeklyShowName;
    if (!weeklyShowName) {
        weeklyShowName = `${formatCompanyNameBackend(company.name)} Weekly`;
    }

    let nextEvent = await getNextEventFromCurrentEvent(currentEvent, parentEvents, company, numberOfWeeklyShows, weeklyShowName);

    // Add next event boost
    if (currentEvent.nextEventBoost && nextEvent.weeklyShow) {
        // Carry over boost until next monthly event
        nextEvent.nextEventBoost = currentEvent.nextEventBoost;
    }

    // Continue allow_other_brands_extended active effect until monthly show
    if (currentEvent.activeEffect === 'allow_other_brands_extended') {
        if (nextEvent.weeklyShow) {
            nextEvent.activeEffect = 'allow_other_brands_extended';
        } else {
            nextEvent.activeEffect = 'allow_other_brands';
        }
    }

    return nextEvent;
}

async function addWeeklyShow(name, monthlyEventName, weekNumber, brand = null) {  // add_weekly_show
    if (brand) {
        name = brand.weeklyShowName;
    }
    let weeklyShow = Event(name);
    await Events.insert(weeklyShow);
    weeklyShow.weeklyShow = true;
    weeklyShow.monthlyEventName = monthlyEventName;
    weeklyShow.weekNumber = weekNumber;
    if (brand && brand.weeklyShowIsExclusive) {
        weeklyShow.brandName = brand.name;
    }
    return weeklyShow;
}

async function addMonthlyEvent(parentEvent, parentEvents) {  // add_monthly_event
    let prestige = parentEvent.prestige;
    let topEvent = parentEvent.$loki === getArrayMaxByProperty(parentEvents, 'xp').$loki;
    let hype = await getEventHype(parentEvent, topEvent);
    let monthlyEvent = Event(parentEvent.name);
    await Events.insert(monthlyEvent);
    monthlyEvent.prestige = prestige;
    monthlyEvent.hype = hype;
    monthlyEvent.brandName = parentEvent.brandName;
    addChildEvent(parentEvent, monthlyEvent);
    return monthlyEvent;
}

function getTopEvent(parentEvents) {
    return parentEvents.reduce((prev, current) => {
        return (prev.xp > current.xp) ? prev : current
    });
}

function getWeeklyEventName(event, nextMonthlyEvent, numberOfWeeklyShows) {  // Previously get_weekly_show_name
    if (event.monthlyEventName) {
        let numberOfShowsRemaining;
        let nextEventName = nextMonthlyEvent.name;

        // Get number of shows remaining till monthly event
        if (event.weekNumber === null) {
            // One show remaining if tournament or cash in weekly show
            numberOfShowsRemaining = 1
        }
        else if (event.weekNumber < 0) {
            // If weekly show before monthly
            numberOfShowsRemaining = Math.abs(event.weekNumber);
            nextEventName = event.monthlyEventName;
        }
        else {
            // If weekly show after monthly
            numberOfShowsRemaining = numberOfWeeklyShows - event.weekNumber + 1
        }

        let showsWord = numberOfShowsRemaining !== 1 ? 'Shows' : 'Show';
        return `${numberOfShowsRemaining} ${showsWord} to ${nextEventName}`
    }
    return null;
}

function getEventMinimumMatchesAndSegments(event, parentEvents, rosterSuperstars) {  // Previously get_event_minimum_matches etc
    let minimumMatchesAndSegments;

    // Get minimum based on event prestige
    if (event.weeklyShow || event.name === 'Weekly Show') {
        // If weekly event
        minimumMatchesAndSegments = 4;
        let monthlyEvent = parentEvents.find(x => x.name === event.monthlyEventName);
        if (monthlyEvent) {
            let minLimits = {"A": 4, "B": 3, "C": 2, "default": 2};
            minimumMatchesAndSegments = (minLimits[monthlyEvent.prestige] || minLimits['default'])
        }
    }
    else {
        // If monthly event
        let minLimits = {"A": 6, "B": 5, "C": 4, "default": 4};
        minimumMatchesAndSegments = (minLimits[event.prestige] || minLimits['default']);

        // Lower minimum if superstars available are low
        let availableSuperstars = rosterSuperstars.filter(x => checkAvailability(x, false) &&
            checkSuperstarBrandAvailability(event, x));
        let superstarsNeeded = minimumMatchesAndSegments * 3;
        if (availableSuperstars.length < superstarsNeeded) {
            minimumMatchesAndSegments = availableSuperstars.length / 3
        }

        if (minimumMatchesAndSegments < 1) {
            minimumMatchesAndSegments = 1
        }
    }

    return parseInt(minimumMatchesAndSegments);
}

function getEventMaximumMatchesAndSegments(event, parentEvents, productionFacility) {
    let maximumMatchesAndSegments;

    // Get maximum based on event prestige
    if (event.weeklyShow || event.name === 'Weekly Show') {
        // If weekly event
        maximumMatchesAndSegments = 6;
        let monthlyEvent = parentEvents.find(x => x.name === event.monthlyEventName);
        if (monthlyEvent) {
            let maxLimits = {"A": 8, "B": 7, "C": 6, "default": 6};
            maximumMatchesAndSegments = (maxLimits[monthlyEvent.prestige] || maxLimits['default'])
        }
    }
    else {
        // If monthly event
        let maxLimits = {"A": 12, "B": 11, "C": 10, "default": 10};
        maximumMatchesAndSegments = (maxLimits[event.prestige] || maxLimits['default']);
    }

    // Increase maximum if artifact used on event
    if (event.activeEffect === 'increase_matches') {
        maximumMatchesAndSegments += 2
    }

    // Increase maximum depending on production facility's level
    if (productionFacility && productionFacility.level >= 2) {
        maximumMatchesAndSegments += (productionFacility.level - 1) * 2
    }

    return maximumMatchesAndSegments;
}

async function getEventMatchesData({event, rosterSuperstars = null, cashinSingles = false, cashinTag = false,
                                   cashinTrios = false, history = false, getChampionshipsAvailable = true,
                                   play = false, game = null}={}) {  // Previously get_matches
    let matchesData = [];
    let eventMatches = await getEventMatches(event);
    let company = await getEventCompany(event);

    if (eventMatches.length > 0) {
        const sortedMatches = eventMatches.sort((a, b) => b.order - a.order);
        const mainRoster = await getCompanyRosterByType(company, 'main');
        if (!rosterSuperstars) {
            rosterSuperstars = await getRosterSuperstars(mainRoster)
        }

        // Get company championships
        const companyChampionships = await getCompanyChampionships(company);

        for (const match of sortedMatches) {
            const matchData = await getMatchData({match, cashinSingles, cashinTag, cashinTrios, history,
                getChampionshipsAvailable, play, companyChampionships, rosterSuperstars, game});
            matchesData.push(matchData);
        }
    }

    return matchesData;
}

async function getEventSegmentsData({event, play = false, history = false, rosterSuperstars = null,
                                    producers = null} = {}) {  // get_segments
    let segmentsData = [];
    let eventSegments = await getEventSegments(event);
    let company = await getEventCompany(event);

    if (eventSegments.length > 0) {
        const sortedSegments = eventSegments.sort((a, b) => b.order - a.order);

        // Get roster superstars if not present
        const mainRoster = await getCompanyRosterByType(company, 'main');
        if (!rosterSuperstars) {
            rosterSuperstars = await getRosterSuperstars(mainRoster)
        }

        for (const segment of sortedSegments) {
            const segmentData = await getSegmentData({segment: segment, pronouns: true, play: play, history: history,
                rosterSuperstars: rosterSuperstars, producers: producers});
            segmentsData.push(segmentData);
        }
    }

    return segmentsData;
}

async function getEventBaseHype(event, isTopEvent = null, rosterBrands = null) {  // Previously get_event_base_hype
    const company = await getEventCompany(event);
    let baseHype;

    // Get hype for weekly show
    if (event.weeklyShow || event.name === 'Weekly Show') {
        baseHype = 0;
        if (company.hot) {
            baseHype += 10
        }
    }
    // Get hype for monthly event
    else {
        const baseHypes = {"A": 60, "B": 20, "C": 5, "default": 0};
        baseHype = (baseHypes[event.prestige] || baseHypes['default']);
        if (isTopEvent) {
            baseHype += 90
        }
        if (company.hot) {
            baseHype += 30
        }
    }

    // Add brand hype
    baseHype += getEventBrandHype(event, rosterBrands);

    return baseHype;
}

function getEventBrandHype(event, rosterBrands) {  // Previously get_event_brand_hype
    let brandHype = 0;

    if (rosterBrands) {
        const eventBrand = getBrandForEvent(event, rosterBrands);
        if (eventBrand) {
            // For weekly shows, add overness effect
            if (event.weeklyShow) {
                brandHype += -35 + myround(eventBrand.overness, 10)
            }
            // For monthly events, add hot/stale effects
            else {
                if (eventBrand.hot) {
                    brandHype += 15
                }
                else if (eventBrand.stale) {
                    brandHype += -20
                }
            }
        }
    }

    return brandHype;
}

function getEventTitle(event) {  // Previously get_event_title
    // Get month name
    let monthName = '';
    if (event.month !== null && !event.temporaryEvent) {
        const monthNames = ["January", "February", "March", "April", "May", "June", "July", "August", "September",
            "October", "November", "December"
        ];
        monthName = ' - ' + monthNames[event.month].slice(0, 3);
    }

    // Get year
    let eventYear = (event.year) ? ' ' + event.year : '';

    // Get title
    return `${event.name}${monthName}${eventYear}`;
}

function getDatetimeForEvent(event, rosterBrands) {  // Previously get_datetime_for_event
    const year = event.year;
    const month = event.month;
    const firstSunday = getFirstSunday(year, month);

    if (event.weeklyShow) {
        // Get day for weekly show based on brand order if present
        const currentBrandIndex = getBrandIndexForWeeklyShow(event, rosterBrands);
        let weeklyEventDay = new Date(firstSunday);
        if (currentBrandIndex === 0 || currentBrandIndex === null) {
            // Get first monday
            weeklyEventDay = new Date(weeklyEventDay.setDate(firstSunday.getDate() + 1))
        }
        else if (currentBrandIndex === 1) {
            // Get first wednesday
            weeklyEventDay = new Date(weeklyEventDay.setDate(firstSunday.getDate() + 3))
        }
        else if (currentBrandIndex === 2) {
            // Get first friday
            weeklyEventDay = new Date(weeklyEventDay.setDate(firstSunday.getDate() + 5))
        }

        // Get increasing weeks based on weeklyEventDay
        if (event.weekNumber === -2) {
            weeklyEventDay.setDate(weeklyEventDay.getDate() + 14);
            return weeklyEventDay;
        }
        else if (event.weekNumber === -1) {
            weeklyEventDay.setDate(weeklyEventDay.getDate() + 21);
            return weeklyEventDay;
        }
        else if (event.weekNumber === 1) {
            return weeklyEventDay;
        }
        else if (event.weekNumber === 2) {
            weeklyEventDay.setDate(weeklyEventDay.getDate() + 7);
            return weeklyEventDay;
        }
    }
    else {
        // Get first sunday for monthly event
        return firstSunday;
    }

    return new Date();
}

function checkEventWithinDatetime(eventDatetime, datetimeToCheck, timePeriod) {  // Previously check_event_within_datetime
    if (timePeriod === 'all') {
        return true
    }
    const numberOfMonths = parseInt(timePeriod);
    let latestDate = new Date(datetimeToCheck.getTime());
    latestDate.setMonth(datetimeToCheck.getMonth() - numberOfMonths);
    return eventDatetime >= latestDate;
}

async function getParentEventForEvent(parentEvents, event) {  // Previously get_parent_event
    let parentEvent = parentEvents.find(x => x.name === event.name);
    if (!parentEvent) {
        parentEvent = await getParentEvent(event)
    }
    return parentEvent;
}

async function getEventPosterData(eventUid, annualTournamentUid) {  // Previously get_event_poster_data_view
    let data = {};
    const currentGame = await getCurrentGame();
    const userCompany = await getGameCurrentUserCompany(currentGame);

    // Get event or annual tournament
    let annualTournament;
    let event;
    if (annualTournamentUid) {
        annualTournament = await Tournaments.get(annualTournamentUid);
        event = await getTournamentEvent(annualTournament)
    }
    else {
        event = await Events.get(eventUid)
    }

    // Check if event found
    if (!event) {
        data['error'] = 'event-not-found';
        return data;
    }

    // Check paid
    if (!globalUser.paid) {
        data['error'] = 'paid-only';
        return data;
    }

    // Check if poster present
    if (!event.posterGraphic || Object.keys(event.posterGraphic).length === 0) {
        data['error'] = 'poster-not-present';
        return data;
    }

    // Get poster data
    const parentEvents = await getCompanyParentEvents(userCompany);
    const parentEvent = await getParentEventForEvent(parentEvents, event);
    const eventMatches = await getEventMatches(event);
    const companyChampionships = await getCompanyChampionships(userCompany);
    data['eventPosterGraphicData'] = await getEventPosterGraphicData(event, eventMatches, companyChampionships, parentEvent,
        annualTournament);

    return data;
}

async function getEventPosterGraphicData(event, eventMatches, companyChampionships, parentEvent,
                                         annualTournament = null) {  // Previously get_event_poster_data
    let data = {};
    let eventRandom = new Math.seedrandom(event.meta.created);  // Use to get poster and keep it the same
    if (annualTournament) {
        companyChampionships = []
    }

    // On night 2 of annual tournament, use night 1 for random instance and to duplicate poster graphic
    if (annualTournament && event.name[event.name.length - 1] === '2') {
        const tournamentMatches = await getTournamentMatches(annualTournament);
        let night1Event;
        for (const tournamentMatch of tournamentMatches) {
            const matchEvent = await getMatchEvent(tournamentMatch);
            if (matchEvent.$loki !== event.$loki) {
                night1Event = matchEvent;
                break;
            }
        }
        eventRandom = new Math.seedrandom(night1Event.meta.created);
        event.posterGraphic = night1Event.posterGraphic;
    }

    // Get superstar weights
    const superstarWeights = await getEventPosterSuperstarWeights(eventMatches, companyChampionships);

    // Get poster type
    let posterTypeData = getRandomChoice(eventPostersData, eventRandom());
    if (annualTournament) {
        posterTypeData = getRandomChoice(annualTournamentPostersData, eventRandom())
    }

    // Load poster graphics if present
    if (event.posterGraphic && Object.keys(event.posterGraphic).length !== 0) {
        data['graphics'] = event.posterGraphic;
        // Match poster data to poster graphics (used for games imported with different event ids)
        if (data['graphics'].length !== posterTypeData['numberOfSuperstars']) {
            posterTypeData = eventPostersData.find(x => data['graphics'].length === x['numberOfSuperstars']);
            if (!posterTypeData) {
                return null  // Don't show poster if not enough superstars
            }
        }
    }
    // Otherwise get graphics
    else {
        // Get poster with fewer number of superstars if not enough for selected poster
        if (superstarWeights.length < posterTypeData['numberOfSuperstars']) {
            posterTypeData = eventPostersData.find(x => superstarWeights.length >= x['numberOfSuperstars']);
            if (!posterTypeData) {
                return null  // Don't show poster if not enough superstars
            }
        }
        const posterSuperstars = getEventPosterSuperstars(eventRandom, superstarWeights, posterTypeData);
        data['graphics'] = getEventPosterGraphics(posterSuperstars, companyChampionships);
    }

    // Get other poster data
    data['styleData'] = getPosterStyleData(event, parentEvent);
    data['eventName'] = event.name;
    data['eventYear'] = annualTournament ? '' : event.year;
    data['type'] = posterTypeData['type'];
    data['annualTournament'] = !!(annualTournament);

    return data;
}

function getEventPosterSuperstars(eventRandom, superstarWeights, posterTypeData) {  // get_event_poster_superstars
    // Get top superstars
    const posterSuperstars = [];
    const numberOfSuperstars = posterTypeData.numberOfSuperstars;

    for (let x = 0; x < numberOfSuperstars; x++) {
        const selection = weightedChoice(superstarWeights, eventRandom());
        posterSuperstars.push(selection);
        superstarWeights = superstarWeights.filter(y => y[0].$loki !== selection.$loki);
    }

    // Get superstars in specific order for annual tournament
    if (posterTypeData.type === 5) {
        posterSuperstars.sort((a, b) => {
            const orderMapping = {
                1: 12, 2: 11, 3: 14, 4: 9, 5: 10, 6: 13, 7: 15, 8: 8,
                9: 2, 10: 5, 11: 1, 12: 7, 13: 4, 14: 3, 15: 6, 16: 0
            };
            return (orderMapping[a.tournamentSeed] ?? 20) - (orderMapping[b.tournamentSeed] ?? 20);
        });
    }
    if (posterTypeData.type === 6) {
        posterSuperstars.sort((a, b) => {
            const orderMapping = {
                1: 13, 2: 14, 3: 15, 4: 12,
                5: 0, 6: 2, 7: 1, 8: 3,
                9: 11, 10: 8, 11: 10, 12: 9,
                13: 5, 14: 7, 15: 6, 16: 4
            };
            return (orderMapping[a.tournamentSeed] ?? 20) - (orderMapping[b.tournamentSeed] ?? 20);
        });
    }
    if (posterTypeData.type === 7) {
        posterSuperstars.sort((a, b) => {
            const orderMapping = {
                1: 2, 2: 3, 3: 1, 4: 4,
                5: 6, 6: 7, 7: 5, 8: 8,
                9: 9, 10: 10, 11: 11, 12: 12,
                13: 13, 14: 14, 15: 15, 16: 16
            };
            return (orderMapping[a.tournamentSeed] ?? 20) - (orderMapping[b.tournamentSeed] ?? 20);
        });
    }

    return posterSuperstars;
}

function getEventPosterGraphics(posterSuperstars, companyChampionships) {  // get_event_poster_graphics
    const graphics = [];

    // Get graphics for top superstars with titles
    for (const superstar of posterSuperstars) {

        // Get attire or graphic if present, otherwise get empty graphic
        let superstarGraphic = JSON.parse(JSON.stringify(superstar.graphic));
        if (superstarGraphic) {
            if (superstar.currentAttire > 0 && superstarGraphic['alternate_attires']) {
                superstarGraphic = superstarGraphic['alternate_attires'][superstar.currentAttire - 1];
            }
        } else {
            superstarGraphic = getEmptySuperstarGraphic(superstar);
        }

        // Get championship graphic
        const superstarChampionship = companyChampionships.find(c => c.superstarId === superstar.$loki);
        if (superstarChampionship && typeof superstarGraphic === 'object') {
            superstarGraphic['title'] = JSON.parse(JSON.stringify(superstarChampionship.graphic));
        }

        graphics.push(superstarGraphic);
    }

    return graphics;
}

async function getEventPosterSuperstarWeights(eventMatches, companyChampionships) {  // Previously get_event_poster_superstar_weights
    const superstarWeights = [];
    const mainEventMatchId = (eventMatches.length) ? eventMatches.reduce((prev, current) => {
        return (prev.order < current.order) ? prev : current
    }).$loki : null;

    // Get superstar weights using event matches
    for (const match of eventMatches) {
        const matchParticipants = await getMatchParticipants(match);
        const matchSuperstars = (await Promise.all(
            matchParticipants.map(x => getRosterMemberSuperstars(x))
        )).flat();

        for (const superstar of matchSuperstars) {
            if (superstarWeights.find(x => x[0].$loki === superstar.$loki)) {
                continue;  // Don't add superstar to weights more than once
            }
            const superstarChampionships = companyChampionships.filter(x => x.superstarId === superstar.$loki);
            let weight = Math.pow(superstar.popularity / 100, 2);
            if (superstar.hot) {
                weight *= 5
            }
            if (superstar.faceOfTheCompany) {
                weight *= 5
            }
            if (superstarChampionships.length > 0) {
                weight *= 10
            }
            if (match.$loki === mainEventMatchId) {
                weight *= 100;
            }
            if (!superstar.graphic || Object.keys(superstar.graphic).length === 0 || superstar.graphic['empty_graphic']) {
                weight /= 1000
            }

            superstarWeights.push([superstar, weight]);
        }
    }

    return superstarWeights;
}

function getPosterStyleData(event, parentEvent) {  // Previously get_poster_style_data
    let data = {};
    let eventRandom = new Math.seedrandom(event.meta.created);
    let parentEventRandom = (parentEvent) ? new Math.seedrandom(parentEvent.meta.created) : Math.random;  // Use to get poster font

    // Get background object
    let backgroundObjectChoices = [[0, 25], [1, 14], [2, 14], [3, 14], [4, 7]];
    if (event.prestige === 'A') {
        // Unique gradient choice for A prestige events
        backgroundObjectChoices.push(...[[5, 10], [6, 10], [7, 10], [8, 10]])
    }
    let backgroundObject = weightedChoice(backgroundObjectChoices, eventRandom());

    // POW premier event background
    if (event.activeEffect === 'premier_event') {
        backgroundObject = 9
    }

    let backgroundColor;
    if (backgroundObject === 0) {
        // Single color solid background
        backgroundColor = getRandomChoice(['151414', '151414', '151414', '151414', '2F1442', '0C2240',
            '400309', '102F3B', '400D26'], eventRandom())
    }
    else if (backgroundObject === 4) {
        // Single color solid background
        backgroundColor = getRandomChoice(['2F1442', '0C2240', '400309', '11324F', '42083E'], eventRandom())
    }
    else {
        if ([1, 2, 3].indexOf(backgroundObject) >= 0) {
            // Gradient background
            backgroundColor = getRandomChoice(['7F6F16', '631919', '182A60', '1F567D', '19425F', '3C1762', '343A70', '3D4D59', '704234', '342B57',
                 '4F2635', '59513D', '28473E'], eventRandom())
        }
        else {
            // Special background, original color only
            backgroundColor = 'FFFFFF';
        }
    }

    // Get font family object and color
    const fontFamilyObject = getRandomInt(0, 6, parentEventRandom());
    let fontColor;
    if (backgroundColor === '151414') {
        // Add colored fonts if solid black background
        fontColor = getRandomChoice(['FFFFFF', 'FFFFFF', 'FFFFFF', 'A51818', '1568D4', '4D44FC',
            '7440B8', 'C8B963', 'BD5909'], eventRandom())
    }
    else {
        fontColor = 'F7F7F7'
    }

    // Get text position
    const textPosition = weightedChoice([['top', 70], ['bottom', 30]], eventRandom());

    // Get data to return
    data['backgroundObject'] = backgroundObject;
    data['backgroundColor'] = backgroundColor;
    data['fontFamilyObject'] = fontFamilyObject;
    data['fontColor'] = fontColor;
    data['textPosition'] = textPosition;

    return data;
}

async function getViewEventsData(companyUid, eventUid) {  // Previously get_events
    let data = {};
    const currentGame = await getCurrentGame();

    // Get selected company
    let selectedCompany;
    if (companyUid) {
        selectedCompany = await Companies.get(companyUid)
    }
    else {
        selectedCompany = await getGameCurrentUserCompany(currentGame)
    }

    // Get events
    const companyEvents = await getCompanyEvents(selectedCompany);
    const parentEvents = companyEvents.slice(0, 12);
    const nonParentEvents = companyEvents.slice(12);

    // Get event by uid
    let event;
    if (eventUid) {
        event = await Events.get(eventUid);
    }
    // Otherwise get latest event
    else {
        event = companyEvents[companyEvents.length - 2];
        if (event.rating === null) {
            event = companyEvents[companyEvents.length - 1]
        }
    }

    data['event'] = await getEventData(event, parentEvents, selectedCompany, currentGame);

    // Get ids for next and previous events if present
    const eventIndex = nonParentEvents.indexOf(nonParentEvents.find(x => x.$loki === event.$loki));
    if (eventIndex < nonParentEvents.length - 2) {
        data['nextEventId'] = nonParentEvents[eventIndex + 1].$loki
    }
    if (eventIndex > 0) {
        data['previousEventId'] = nonParentEvents[eventIndex - 1].$loki
    }

    // Get year/uid for each year event was held
    data['years'] = [];
    if (!event.weeklyShow) {
        const eventIterations = await getEventIterations(event, nonParentEvents);
        data['years'] = eventIterations.filter(y => y.year !== null && y.rating !== null).map(x => {
            return {'year': x.year, 'uid': x.$loki}
        });
        data['years'] = [...new Set(data['years'])];
    }

    // Get prestige info for monthly event
    const parentEvent = await getParentEventForEvent(parentEvents, event);
    if (parentEvent) {
        data['event']['prestige'] = parentEvent.prestige;
        data['event']['xp'] = parentEvent.xp;
        data['event']['baseXp'] = eventPrestigeXps[parentEvent.prestige];
        data['event']['xpToNextPrestige'] = (parentEvent.prestige === 'C') ? 40 : 110;
        data['event']['topEvent'] = parentEvent.$loki === getTopEvent(parentEvents).$loki;

        // Get match contracts for this event
        const matchContracts = await getEventMatchContracts(parentEvent);
        data['event']['matchContracts'] = await Promise.all(matchContracts.map(x => getMatchContractData(x)));

        // Get streaks and match effects if paid
        data['event']['streaks'] = [];
        data['event']['matchEffects'] = [];
        if (globalUser.paid) {
            if (parentEvent.prestige === 'A') {
                const streaks = await getEventStreaks(parentEvent);
                data['event']['streaks'] = await Promise.all(streaks.map(x => getStreakData(x)));
                data['event']['streaks'].sort((a, b) => b.length - a.length);
            }

            // Get match effects
            const eventGimmickMatchPoints = await getEventGimmickMatchPoints(event, companyEvents);
            for (const [key, value] of Object.entries(eventGimmickMatchPoints)) {
                data['event']['matchEffects'].push(
                    {'gimmickMatch': key, 'bonus': getGimmickMatchBonusFromPoints(value), 'points': value}
                )
            }
            data['event']['matchEffects'].sort((a, b) => {
              if (a.bonus !== 0 && b.bonus === 0) return -1;
                if (a.bonus === 0 && b.bonus !== 0) return 1;
                return b.bonus - a.bonus;
            });
        }
    }

    return data;
}

async function getEventIterations(event, nonParentEvents) {  // Previously get_event_iterations
    let eventIterations = nonParentEvents.filter(x => x.name === event.name);

    // Get child/sibling events
    let childEvents;
    const eventParent = await getParentEvent(event);
    if (eventParent) {
        childEvents = await getChildEvents(eventParent);
    }
    else {
        childEvents = await getChildEvents(event);
    }
    eventIterations.push(...childEvents);
    eventIterations = getUniqueObjects(eventIterations);
    eventIterations.sort((a, b) => a.$loki - b.$loki);

    return eventIterations;
}

async function getEventData(event, parentEvents, company, game) {  // Previously get_event_data
    let data = {};

    data['name'] = event.name;
    data['year'] = event.year;
    data['rating'] = event.rating;
    data['pointsEarned'] = event.pointsEarned;
    data['fansGained'] = event.fansGained;
    data['hype'] = event.hype;
    data['viewership'] = event.viewership;
    data['weeklyShow'] = event.weeklyShow;
    data['brandName'] = event.brandName;
    data['weeklyShowBeforeName'] = event.weeklyShowBeforeName;
    data['weeklyShowAfterName'] = event.weeklyShowAfterName;
    data['uid'] = event.$loki;
    data['companyName'] = company.name;
    data['name'] = event.name;

    // Get matches and segments
    const matches = await getEventMatchesData({event: event, history: true, getChampionshipsAvailable: false});
    const segments = await getEventSegmentsData({event: event, history: true});
    data['matchesAndSegments'] = matches.concat(segments);
    data['matchesAndSegments'].sort((a, b) => a.order - b.order || a.segmentType.localeCompare(b.segmentType));

    // Get weekly show's name
    if (event.weeklyShow) {
        const nextMonthlyEvent = await getUpcomingMonthlyEventForWeeklyEvent(event, parentEvents, company);
        data['weeklyShowName'] = getWeeklyEventName(event, nextMonthlyEvent, game.numberOfWeeklyShows);

        // Get nielsen rating for weekly show
        if (event.viewership !== null) {
            data['nielsenRating'] = getNielsenRatingFromViewership(event.viewership)
        }
    }

    return data;
}

async function getCompanyEventsData(companyUid) {  // Previously get_company_events
    let data = {};
    const currentGame = await getCurrentGame();

    // Get selected company
    let selectedCompany;
    if (companyUid) {
        selectedCompany = await Companies.get(companyUid)
    }
    else {
        selectedCompany = await getGameCurrentUserCompany(currentGame)
    }

    // Get events
    const companyEvents = await getCompanyEvents(selectedCompany);
    const parentEvents = companyEvents.slice(0, 12);
    const nextEvent = companyEvents[companyEvents.length - 1];

    // Get data for parent events
    data['events'] = await Promise.all(parentEvents.map(async x => {
        const childEvents = (await getChildEvents(x)).filter(y => y.rating !== null);
        // Get latestChildEvent if held for calendar click
        let latestChildEventId = (childEvents.length > 0) ? childEvents[childEvents.length - 1].$loki : null;
        return {'name': x.name, 'uid': x.$loki, 'brandName': x.brandName, 'latestChildEventUid': latestChildEventId}
    }));

    // Get upcoming event's name
    if (nextEvent.weeklyShow || nextEvent.name === 'Weekly Show') {
        const upcomingMonthlyEvent = await getUpcomingMonthlyEventForWeeklyEvent(nextEvent, parentEvents, selectedCompany);
        data['upcomingEventName'] = upcomingMonthlyEvent.name;
    }
    else {
        data['upcomingEventName'] = nextEvent.name;
    }

    // Get company weekly show name
    let companyWeeklyShowName = selectedCompany.weeklyShowName;
    if (!companyWeeklyShowName) {
        companyWeeklyShowName = `${formatCompanyNameBackend(selectedCompany.name)} Weekly`
    }
    data['companyWeeklyShowName'] = companyWeeklyShowName;

    // Get brand names
    const mainRoster = await getCompanyRosterByType(selectedCompany, 'main');
    const rosterBrands = await getRosterBrands(mainRoster);
    data['brandNames'] = rosterBrands.map(x => x.name);

    return data;
}

function getNielsenRatingFromViewership(viewership) {  // Previously get_nielsen_rating_from_viewership
    return decimalRound(viewership * 0.8 / 1000000)
}

async function rebrandEvent(uid, name, weeklyShow, brandName) {  // Previously rebrand_event_view
    let data = {};
    const currentGame = await getCurrentGame();
    const userCompany = await getGameCurrentUserCompany(currentGame);
    const cost = 50;
    const nextEvent = await getCompanyLatestEvent(userCompany);
    const mainRoster = await getCompanyRosterByType(userCompany, 'main');
    const rosterBrands = await getRosterBrands(mainRoster);
    const companyEvents = await getCompanyEvents(userCompany);
    const parentEvents = companyEvents.slice(0, 12);
    const nonParentEvents = companyEvents.slice(12);

    // Remove multiple spaces in name
    name = name.replace(/ +(?= )/g,'');

    // Check points
    if (userCompany.points < cost) {
        data['error'] = 'points';
        return data;
    }

    // Check for WoM
    if (userCompany.storyName === 'mars') {
        data['error'] = 'mars';
        return data;
    }

    // Get event
    let event;
    if (!weeklyShow) {
        event = await Events.get(uid)
    }

    // Check for same name
    if (event && name === event.name) {
        data['error']  ='same-name';
        return data;
    }
    // Check for same weekly show name
    if (!event && name === userCompany.weeklyShowName) {
        data['error']  ='same-name';
        return data;
    }

    // Check if name already exists
    const existingEventNames = parentEvents.map(x => x.name);
    if (userCompany.weeklyShowName) {
        existingEventNames.push(userCompany.weeklyShowName)
    }
    if (existingEventNames.indexOf(name) >= 0) {
        data['error'] = 'name-exists';
    }

    // Change weekly show name
    let previousName;
    if (weeklyShow) {
        previousName = userCompany.weeklyShowName;
        if (!previousName) {
            previousName = `${formatCompanyNameBackend(userCompany.name)} Weekly`
        }
        userCompany.weeklyShowName = name;

        // Change next event's name if weekly show and not brand show
        const brandWeeklyShowNames = rosterBrands.map(x => x.weeklyShowName);
        if (nextEvent.weeklyShow && brandWeeklyShowNames.indexOf(nextEvent.name) < 0) {
            nextEvent.name = name;
            data['updateEvent'] = true;
        }
    }
    // Change monthly event name
    else {
        // Lower prestige by 1, update xp and hype
        const prestiges = ['A', 'B', 'C'];
        const hypes = {'A': 80, 'B': 40, 'C': 15};
        let newPrestigeIndex = prestiges.indexOf(event.prestige) + 1;
        if (newPrestigeIndex >= prestiges.length) {
            newPrestigeIndex -= 1
        }
        event.prestige = prestiges[newPrestigeIndex];
        event.xp = eventPrestigeXps[event.prestige] || 0;
        event.hype = hypes[event.prestige] || 15;

        // Change name
        previousName = event.name;
        event.name = name;

        // Change brand name
        if (brandName) {
            setObjectBrand(event, brandName)
        }
        else {
            removeObjectBrand(event)
        }

        // Add child events by name if they don't already have a parent
        let eventIterations = nonParentEvents.filter(x => x.name === previousName);
        for (const x of eventIterations) {
            const eventParent = await getParentEvent(x);
            if (!eventParent) {
                await addChildEvent(event, x)
            }
        }

        // Change name, prestige, brand name of upcoming show if next
        if (!nextEvent.weeklyShow && nextEvent.name === previousName) {
            nextEvent.name = name;
            nextEvent.prestige = event.prestige;
            nextEvent.xp = event.xp;
            nextEvent.hype = event.hype;
            nextEvent.brandName = event.brandName;
            data['updateEvent'] = true;
        }

        // Change name only of weekly show before monthly show
        if (nextEvent.weeklyShow && nextEvent.weekNumber < 0 && nextEvent.monthlyEventName === previousName) {
            nextEvent.monthlyEventName = name;
        }
    }

    // Remove points
    userCompany.points -= cost;

    // Add company log
    const newCompanyLog = CompanyLog(currentGame.monthCount, userCompany.realYear);
    await CompanyLogs.insert(newCompanyLog);
    newCompanyLog.text = `Rebranded ${previousName} to ${name}`;
    newCompanyLog.type = 'rebrand-event';
    newCompanyLog.realYear = currentGame.year;
    await addCompanyCompanyLogs(userCompany, [newCompanyLog]);

    db.saveDatabase();

    // Get data to return
    data['previousName'] = previousName;
    data['newName'] = name;
    data['points'] = userCompany.points;

    // Save to local storage
    await saveGameDataToLocalStorage(currentGame);

    return data;
}

async function getStreakData(streak) {  // Previously get_streak_data
    let data = {};

    data['holder'] = (await getStreakTalent(streak))?.name;
    data['length'] = streak.length;
    data['event'] = (await getStreakEvent(streak))?.name;
    data['uid'] = streak.$loki;

    return data;
}

async function getMatchContractData(matchContract) {  // Previously get_match_contract
    let data = {};

    data['holder'] = (await getMatchContractTalent(matchContract))?.name;
    data['name'] = matchContract.name;
    data['bonus'] = matchContract.bonus;
    data['event'] = (await getMatchContractEvent(matchContract))?.name;
    data['uid'] = matchContract.$loki;

    return data;
}

async function getEventGimmickMatchPoints(event, allEvents) {  // Previously get_event_match_bonuses
    let eventIterations = allEvents.filter(x => x.name === event.name && x.rating !== null);
    const gimmickMatchesPoints = {};

    // Go through event iterations, finding gimmick matches
    for (const eventIteration of eventIterations) {
        const eventMatches = await getEventMatches(eventIteration);
        for (const match of eventMatches) {
            const gimmickMatch = await getMatchGimmickMatch(match);
            if (gimmickMatch && match.rating !== null) {
                // Get points for each time gimmick match used
                const points = getGimmickMatchPoints(match.rating);
                if (gimmickMatchesPoints[gimmickMatch.name]) {
                    gimmickMatchesPoints[gimmickMatch.name] += points
                }
                else {
                    gimmickMatchesPoints[gimmickMatch.name] = points;
                }
            }
        }
    }

    return gimmickMatchesPoints;
}

function getGimmickMatchPoints(rating) {
    let points;
    let ratingValue = 4 - rating;
    if (rating > 4) {
        ratingValue *= -1;
        points = Math.pow(ratingValue, 0.5)
    }
    else if (rating === 4) {
        points = Math.pow(0.125, 0.5)
    }
    else {
        points = Math.pow(ratingValue, 0.5) * -1
    }
    return Math.floor(points * 100);
}

function getGimmickMatchBonusFromPoints(points) {  // Previously get_gimmick_match_bonus
    let bonus;
    if (points < -500) {
        bonus = -50
    }
    else if (points < -340) {
        bonus = -25
    }
    else if (points < -170) {
        bonus = -12
    }
    else if (points < 170) {
        bonus = 0
    }
    else if (points < 340) {
        bonus = 12
    }
    else if (points < 500) {
        bonus = 25
    }
    else {
        bonus = 50
    }
    return bonus;
}

async function getEventCardData() {  // Previously get_latest_event_data / part of game_loop
    let data = {};
    let eventData = {};
    const currentGame = await getCurrentGame();
    const company = await getGameCurrentUserCompany(currentGame);
    const nextEvent = await getCompanyLatestEvent(company);
    const companyEvents = await getCompanyEvents(company);
    const parentEvents = companyEvents.slice(0, 12);
    const mainRoster = await getCompanyRosterByType(company, 'main');
    const rosterSuperstars = await getRosterSuperstars(mainRoster);
    const rosterBrands = await getRosterBrands(mainRoster);
    const productionFacility = (await getCompanyFacilities(company)).find(x => x.name === 'Production');
    const companyStaffMembers = await getCompanyStaff(company);

    // Check if top event
    const topEvent = getTopEvent(parentEvents);
    const isTopEvent = nextEvent.name === topEvent.name;

    // Get next event data
    eventData.name = nextEvent.name;
    eventData.activeEffect = nextEvent.activeEffect;
    eventData.brandName = nextEvent.brandName;
    eventData.brandColor = getBrandColorForEvent(nextEvent, rosterBrands);
    eventData.prestige = nextEvent.prestige;
    eventData.baseHype = await getEventBaseHype(nextEvent, isTopEvent, rosterBrands);
    if (nextEvent.weeklyShow || nextEvent.name === 'Weekly Show') {
        eventData.weeklyShow = true;
        const nextMonthlyEvent = await getUpcomingMonthlyEventForWeeklyEvent(nextEvent, parentEvents, company);
        eventData.weeklyShowName = getWeeklyEventName(nextEvent, nextMonthlyEvent, currentGame.numberOfWeeklyShows);
    }

    // Get match and segment limits
    data['minimumMatchesAndSegments'] = getEventMinimumMatchesAndSegments(nextEvent, parentEvents, rosterSuperstars);
    data['maximumMatchesAndSegments'] = getEventMaximumMatchesAndSegments(nextEvent, parentEvents, productionFacility);

    // Get matches and segments
    const producers = getAvailableStaffMembersByRole(companyStaffMembers, 'producer');
    const matches = await getEventMatchesData({event: nextEvent, rosterSuperstars: rosterSuperstars});
    const segments = await getEventSegmentsData({event: nextEvent, rosterSuperstars: rosterSuperstars,
        producers: producers});
    data['matchesAndSegments'] = matches.concat(segments);
    data['matchesAndSegments'].sort((a, b) => a.order - b.order);

    // Get prestige info for monthly event
    eventData['streaks'] = [];
    eventData['matchEffects'] = [];
    eventData['matchContracts'] = [];
    if (nextEvent.name !== 'Weekly Show' && !nextEvent.weeklyShow && !nextEvent.temporaryEvent) {
        const parentEvent = await getParentEventForEvent(parentEvents, nextEvent);

        // Get match contracts for this event
        const matchContracts = (await getEventMatchContracts(parentEvent)).filter(x => x.awarded);
        for (const matchContract of matchContracts) {
            const matchContractData = await getMatchContractData(matchContract);
            eventData['matchContracts'].push(matchContractData);

            // Remove match contract championship TODO remove reigns and random events?
            const matchContractChampionship = await getMatchContractChampionship(matchContract);
            if (matchContractChampionship) {
                const matchContractChampionships = (await getCompanyChampionships(company))
                    .filter(x => checkAreChampionshipsEqual(x, matchContractChampionship));
                for (const c of matchContractChampionships) {
                    await removeCompanyChampionships(company, [c]);
                    await Championships.remove(c);
                    db.saveDatabase();
                }
            }
        }

        // Get streaks and match effects if paid
        if (globalUser.paid) {
            if (parentEvent.prestige === 'A') {
                const streaks = await getEventStreaks(parentEvent);
                eventData['streaks'] = await Promise.all(streaks.map(x => getStreakData(x)));
                eventData['streaks'].sort((a, b) => b.length - a.length);
            }

            const eventGimmickMatchPoints = await getEventGimmickMatchPoints(nextEvent, companyEvents);
            for (const [key, value] of Object.entries(eventGimmickMatchPoints)) {
                eventData['matchEffects'].push(
                    {'gimmickMatch': key, 'bonus': getGimmickMatchBonusFromPoints(value), 'points': value}
                )
            }
            eventData['matchEffects'].sort((a, b) => {
              if (a.bonus !== 0 && b.bonus === 0) return -1;
                if (a.bonus === 0 && b.bonus !== 0) return 1;
                return b.bonus - a.bonus;
            });
        }
    }

    data['eventData'] = eventData;

    return data;
}

async function getEventMatchesAndSegments(event) {
    return (await getEventMatches(event)).concat(await getEventSegments(event));
}

async function playEvent({skipTrainingRosterCallup = false, simulating = false, eventMatches = null,
                          eventSegments = null} = {}) {  // start_event
    let data = {};
    try {
        const currentGame = await getCurrentGame();
        const userCompany = await getGameCurrentUserCompany(currentGame);
        const mainCompany = await getGameMainUserCompany(currentGame);
        const companyEvents = await getCompanyEvents(userCompany);
        const event = companyEvents[companyEvents.length - 1];
        const companyRosters = await getCompanyRosters(userCompany);
        const parentEvents = await getCompanyParentEvents(userCompany);
        const companyChampionships = await getCompanyChampionships(userCompany);
        const mainRoster = await getCompanyRosterByType(userCompany, 'main', companyRosters);
        const trainingAvailableRoster = await getCompanyRosterByType(userCompany, 'trainingAvailable', companyRosters);
        const rosterSuperstars = await getRosterSuperstars(mainRoster);
        const rosterBrands = await getRosterBrands(mainRoster);
        const weeklyShow = event.weeklyShow || event.name === 'Weekly Show' || event.temporaryEvent;
        const parentEvent = await getParentEventForEvent(parentEvents, event);

        // Get matches and segments if not present
        if (!eventMatches) {
            eventMatches = await getEventMatches(event);
        }
        if (!eventSegments) {
            eventSegments = await getEventSegments(event);
        }

        // Get event tournament
        let tournaments = await getEventTournaments(event);
        let tournament;
        if (tournaments.length > 0) {
            tournament = tournaments[0]
        }
        let annualTournament;
        if (tournament && tournament.annualTournament) {
            annualTournament = tournament;
        }

        // Check for unused training roster callup if not simulating or annual tournament
        if (!skipTrainingRosterCallup && !simulating && !annualTournament) {
            if (!userCompany.trainingCallupRun && [0, 6].includes(getCompanyMonth(userCompany, currentGame))) {
                if ((await getRosterSuperstars(trainingAvailableRoster)).length > 0) {
                    data['error'] = 'training-callup-available';
                    return data;
                }
            }
        }

        // Checks to return cash in option with match
        let cashinSingles = false;
        let cashinTag = false;
        let cashinTrios = false;

        // Checks to see if cash in is available
        let cashinSinglesAvailable = false;
        let cashinTagAvailable = false;
        let cashinTriosAvailable = false;

        // Check for championship contracts
        for (const championship of companyChampionships) {
            if (championship.championshipContract && !championship.vacated) {
                if (championship.tagTeam || championship.trio) {
                    if (championship.tagTeam) {
                        cashinTag = true;
                        cashinTagAvailable = true;
                    }
                    else {
                        cashinTrios = true;
                        cashinTriosAvailable = true;
                    }
                }
                else {
                    cashinSingles = true;
                    cashinSinglesAvailable = true;
                }
            }
        }

        // Check for potential championship contracts
        for (const match of eventMatches) {
            for (const championship of (await getMatchChampionships(match))) {
                if (championship.championshipContract && championship.vacated) {
                    if (championship.tagTeam || championship.trio) {
                        if (championship.tagTeam) {
                            cashinTag = true;
                        }
                        else {
                            cashinTrios = true;
                        }
                    }
                    else {
                        cashinSingles = true;
                    }
                }
            }
        }

        // Add event prestige effect bonuses
        if (!weeklyShow) {
            if (globalUser.paid) {
                // Add streak bonus
                if (event.prestige === 'A') {
                    let streaks = await getEventStreaks(parentEvent);
                    for (const match of eventMatches) {
                        let streakBonus = 0;
                        let matchParticipants = await getMatchParticipants(match);
                        for (const streak of streaks) {
                            let streakHolder = await getStreakTalent(streak);
                            if (objectInArray(streakHolder, matchParticipants)) {
                                streakBonus += getStreakBonus(streak.length)
                            }
                        }
                        if (streakBonus) {
                            match.streakBonus = streakBonus;
                        }
                    }
                }

                // Add match effect bonus
                let matchEffects = await getEventGimmickMatchPoints(event, companyEvents);
                for (const match of eventMatches) {
                    const matchGimmick = await getMatchGimmickMatch(match);
                    for (const [key, value] of Object.entries(matchEffects)) {
                        if (matchGimmick && matchGimmick.name === key) {
                            match.eventBonus = getGimmickMatchBonusFromPoints(value);
                        }
                    }
                }
            }

            // Add match contract bonus
            let matchContracts = await getEventMatchContracts(parentEvent);
            for (const match of eventMatches) {
                if ((await getMatchChampionships(match)).length > 0) {
                    let matchContractBonus = 0;
                    let matchParticipants = await getMatchParticipants(match);
                    for (const matchContract of matchContracts) {
                        if (matchContract.awarded && objectInArray((await getMatchContractTalent(matchContract)), matchParticipants)) {
                            matchContractBonus += matchContract.bonus
                        }
                    }
                    if (matchContractBonus) {
                        match.contractBonus = matchContractBonus;
                    }
                }
            }
        }

        // Add hype for unplayed matches
        for (const match of eventMatches) {
            if (match.winner === null) {
                match.hype = await getHypeForMatch((await getMatchParticipants(match)), (await getMatchFeuds(match)),
                    (await getMatchChampionships(match)), userCompany.style, annualTournament, match.tournamentRound);
            }
        }

        // Get matches and segments if not simulating
        data['matchesAndSegmentsData'] = [];
        if (!simulating) {
            let eventMatchesData = await getEventMatchesData({
                event: event,
                cashinSingles: cashinSingles,
                cashinTag: cashinTag,
                cashinTrios: cashinTrios,
                getChampionshipsAvailable: false,
                play: true,
                rosterSuperstars: rosterSuperstars,
                game: currentGame
            });
            let eventSegmentsData = await getEventSegmentsData({
                event: event,
                play: true,
                rosterSuperstars: rosterSuperstars
            });
            let matchesAndSegmentsData = eventMatchesData.concat(eventSegmentsData);
            matchesAndSegmentsData.sort((a, b) => b.order - a.order);

            // Check for minimum number (don't check during tutorial)
            let minimumMatchesAndSegments = getEventMinimumMatchesAndSegments(event, parentEvents, rosterSuperstars);
            let tutorialCheck = userCompany.storyName === 'tutorial' &&
                ['play_first_event', 'simulate_match'].includes(await getTutorialStateName(userCompany));
            if (globalUser.developerMode || tutorialCheck) {
                minimumMatchesAndSegments = 0
            }
            if (matchesAndSegmentsData.length < minimumMatchesAndSegments) {
                data['limit'] = minimumMatchesAndSegments;
                data['error'] = 'min-limit';
                return data;
            }

            // Remove played
            matchesAndSegmentsData = matchesAndSegmentsData.filter(x => {
                return x['segmentType'] === 'segment' ? x['rating'] === null : x['winnerName'] === null;
            });
            data['matchesAndSegmentsData'] = matchesAndSegmentsData;
        }

        // Get event hype if no matches or segments played
        let eventStartedBefore = checkEventStartedBefore(eventMatches.concat(eventSegments));
        if (!eventStartedBefore) {
            let topEvent = event.name === getTopEvent(parentEvents).name;
            event.hype = await getEventHype(event, topEvent, eventMatches.concat(eventSegments),
                companyEvents[companyEvents.length - 2], rosterBrands);
        }

        // Get event preview if no matches or segments played and monthly event or annual tournament
        if (!eventStartedBefore && (!weeklyShow || annualTournament)) {
            try {
                data['eventPreviewData'] = await getEventPreviewData(event, userCompany.name, parentEvents,
                    companyChampionships, annualTournament, mainCompany, currentGame);
            } catch (error) {
                captureError(error)
            }
        }

        // Add annual tournament opening segment
        if (annualTournament && event.name.endsWith('1') && !eventStartedBefore) {
            let annualTournamentOpeningSegment = getAnnualTournamentOpeningSegmentData(currentGame, annualTournament);
            data['matchesAndSegmentsData'].splice(0, 0, annualTournamentOpeningSegment)
        }

        if (!simulating) {
            db.saveDatabase();
        }

        // Get data to return
        data['activeEffect'] = event.activeEffect;
        data['hype'] = event.hype;
        data['eventName'] = event.name;
        data['cashinData'] = {
            'cashinSinglesAvailable': cashinSinglesAvailable,
            'cashinTagAvailable': cashinTagAvailable,
            'cashinTriosAvailable': cashinTriosAvailable,
        };
        if (tournament) {
            data['tournamentDisplay'] = await getTournamentDisplayData(tournament, currentGame)
        }
    }
    catch (error) {
        data['error'] = true;

        // Check if any empty teams present, return in error
        const currentGame = await getCurrentGame();
        const userCompany = await getGameCurrentUserCompany(currentGame);
        const latestEvent = await getCompanyLatestEvent(userCompany);
        const eventMatchesAndSegments = await getEventMatchesAndSegments(latestEvent);
        for (const matchOrSegment of eventMatchesAndSegments) {
            const tagTeams = matchOrSegment.objectName === 'match' ?
                await getMatchTagTeams(matchOrSegment) : await getSegmentTagTeams(matchOrSegment);
            for (const tagTeam of tagTeams) {
                if (!(await getTagTeamSuperstars(tagTeam)).length) {
                    data['error'] = 'team-members-not-found';
                    data['teamName'] = tagTeam.name;
                    break;
                }
            }
        }

        if (data['error'] !== 'team-members-not-found') {
            captureError(error);
        }
    }

    return data;
}

function getStreakBonus(length) {  // get_streak_bonus
    let bonus;
    if (length < 2) {
        return 0
    }

    if (length < 5) {
        bonus = 12
    }
    else if (length < 10) {
        bonus = 25
    }
    else if (length < 20) {
        bonus = 50
    }
    else {
        bonus = 75
    }

    return bonus;
}

function checkEventStartedBefore(matchesAndSegments) {  // check_event_started_before
    let check = false;
    for (const x of matchesAndSegments) {
        if (x.objectName === 'segment') {
            if (x.rating !== null) {
                check = true
            }
        }
        else {
            if (x.winner !== null) {
                check = true
            }
        }
    }
    return check;
}

async function getEventHype(event, topEvent = false, matchesAndSegments = null, previousEvent = null,
                            rosterBrands = null) {  // get_event_hype
    // Get base hype
    let baseHype = await getEventBaseHype(event, topEvent, rosterBrands);

    // Get average of matches and segments hypes
    let matchesAndSegmentsHype = 0;
    if (matchesAndSegments && matchesAndSegments.length) {
        matchesAndSegmentsHype = matchesAndSegments.map(x => getMatchOrSegmentHype(x))
            .reduce((partialSum, a) => partialSum + a, 0) / matchesAndSegments.length;
    }

    // Get event boost from previous event
    let eventHypeBoost = previousEvent ? previousEvent.nextEventBoost : 0;

    return Math.round(baseHype + matchesAndSegmentsHype + eventHypeBoost);
}

function getMatchOrSegmentHype(matchOrSegment) {  // get_match_or_segment_hype
    if (matchOrSegment.hype !== null) {
        if (matchOrSegment.objectName === 'segment') {
            return matchOrSegment.hype;
        }
        else {
            return getMatchHype(matchOrSegment)
        }
    }
    return 0;
}

async function getSuperstarsUsedAtEvent(matchesAndSegments, rosterSuperstars = null) { // get_superstars_used_at_event
    const superstarsUsedAtEvent = [];
    for (const x of matchesAndSegments) {
        let participants;
        if (x.objectName === 'segment') {
            participants = await getSegmentParticipants(x)
        }
        else if (x.objectName === 'match') {
            participants = await getMatchParticipants(x)
        }
        for (const p of participants) {
            for (const s of (await getRosterMemberSuperstars(p))) {
                superstarsUsedAtEvent.push(s);
                const manager = await getSuperstarManagerUsedForMatchOrSegment(s, x.managersUsed, rosterSuperstars);
                if (manager) {
                    superstarsUsedAtEvent.push(manager);
                }
            }
        }
    }
    return getUniqueObjects(superstarsUsedAtEvent);
}

async function endEventRequest() {  // _end_ppv
    const currentGame = await getCurrentGame();
    const userCompany = await getGameCurrentUserCompany(currentGame);
    const user = loadUser();
    let data = {};

    try {
        data = await endEvent(userCompany, user);

        // Check tutorial action
        if (userCompany.storyName === 'tutorial') {
            data['nextTutorialState'] = await checkToGoToNextTutorialState(userCompany, ['end_first_event',
                'end_second_event'])
        }

        db.saveDatabase();

        // Save to local storage
        await saveGameDataToLocalStorage(currentGame);
    }
    catch (error) {
        captureError(error)
        data['error'] = true;
        await loadDb(db.filename);
    }

    return data;
}

function calculateEventRating(matchesAndSegments) { // calculate_event_rating
    let eventMatchesAndSegments = [...matchesAndSegments].sort((a, b) => b.order - a.order);

    // Remove cashins
    eventMatchesAndSegments = eventMatchesAndSegments.filter(x => x.rating !== null);

    let matchesToRemove = Math.round(eventMatchesAndSegments.length * 0.2);
    for (let x = 0; x < matchesToRemove; x++) {
        let minimum = getArrayMinByProperty(eventMatchesAndSegments, 'rating');
        if (minimum.rating < 2) {
            continue;
        }
        if (minimum !== eventMatchesAndSegments[eventMatchesAndSegments.length - 1]) {
            eventMatchesAndSegments.splice(eventMatchesAndSegments.indexOf(minimum), 1);
        }
    }

    // Add main event weight (2x)
    if (eventMatchesAndSegments.length > 0) {
        eventMatchesAndSegments.push(eventMatchesAndSegments[eventMatchesAndSegments.length - 1]);
    }

    if (eventMatchesAndSegments.length === 0) {
        return 0;
    }

    let average = eventMatchesAndSegments.reduce((sum, match) => sum + match.rating, 0) / eventMatchesAndSegments.length;
    return Math.round(average * 4) / 4;  // Round to nearest 0.25
}

async function getEventPreviewData(event, companyName, parentEvents, companyChampionships, annualTournament, mainCompany,
                                   game) {  // get_event_preview_data
    // Generate event description (hype, signature event, main event, exciting matches, top feuds, streaks, match effects,
    // fixed match contract)
    const data = {};
    let parentEvent = await getParentEventForEvent(parentEvents, event);
    if (!parentEvent) {
        parentEvent = await getParentEvent(event);
    }
    const eventMatches = await getEventMatches(event);
    const sortedMatches = [...eventMatches].sort((a, b) => a.order - b.order);
    let mainEventMatch = null;
    if (sortedMatches.length) {
        mainEventMatch = sortedMatches[0];
    }
    const isTopEvent = event.name === getArrayMaxByProperty(parentEvents, 'xp').name;

    // Get poster data
    data['eventPosterData'] = await getEventPosterGraphicData(event, eventMatches, companyChampionships, parentEvent,
        annualTournament);

    // Set graphic if user is paid
    if (globalUser.paid && data['eventPosterData']) {
        event.posterGraphic = data['eventPosterData']['graphics'];
    }

    if (annualTournament) {
        // Get event information
        data['eventName'] = event.name;
        data['eventYear'] = '';

        // Get preview texts
        data['previewTexts'] = await getAnnualTournamentPreviewTexts(event, eventMatches, annualTournament, mainCompany, game);

        // Get match predictions
        data['predictionsData'] = await getAnnualTournamentMatchPredictions(eventMatches, event.name, annualTournament);
    }
    else {
        // Get event information
        data['eventName'] = event.name;
        data['eventYear'] = event.year;
        data['activeEffect'] = event.activeEffect;

        // Get hype level
        const baseHype = await getEventBaseHype(event, isTopEvent);
        const hypeDifference = event.hype - baseHype;
        let hypeWord, audienceWord;
        if (hypeDifference <= 0) {
            hypeWord = 'low';
            audienceWord = 'half packed audience.';
        } else if (hypeDifference <= 50) {
            hypeWord = 'on par';
            audienceWord = 'mostly packed audience.';
        } else {
            hypeWord = 'high';
            audienceWord = 'fully packed audience!';
        }

        // Intro text
        let premierEventText = '';
        if (event.activeEffect === 'premier_event') { // POW story premier event
            premierEventText = ' <span class="font-bold">their premier event</span>,';
        }
        let topEventText = '';
        if (isTopEvent && !premierEventText) {
            topEventText = ' <span class="font-bold">their biggest event of the year</span>,';
        }
        let companyOrBrandName = companyName;
        if (event.brandName) {
            companyOrBrandName = event.brandName;
        }
        data['introText'] = `${companyOrBrandName} presents ${event.name},${topEventText}${premierEventText} live ` +
            `tonight in front of a ${audienceWord}`;

        // Interest / main event text
        let mainEventText = '';
        if (mainEventMatch && (await getMatchParticipants(mainEventMatch)).length) {
            mainEventText = `, which is headlined by ${await getEventPreviewMatchText(mainEventMatch, true)}`;
        }
        data['interestText'] = `Fan interest is ${hypeWord} for the event${mainEventText}.`;

        // Top matches text (exclude matches without participants e.g tournament finals)
        let topMatchesText = '';
        let topMatches = [];
        for (const x of eventMatches) {
            if ((!mainEventMatch || x.$loki !== mainEventMatch.$loki) && (await getMatchParticipants(x)).length) {
                topMatches.push(x)
            }
        }
        topMatches = [...topMatches].sort((a, b) => b.hype - a.hype).slice(0, 3);

        if (topMatches.length) {
            const topMatchPreviewText = await getEventPreviewMatchText(topMatches[0]);
            topMatchesText += `One of the most anticipated matches of the night is ${topMatchPreviewText}.`;
            // If more than 2 top matches, get their preview texts as well
            if (topMatches.length > 2) {
                const topMatchesPreviewText = commaSeparate(await Promise.all(topMatches.slice(1).map(x => getEventPreviewMatchText(x))));
                topMatchesText += ` Other matches that have the fans buzzing include ${topMatchesPreviewText}.`;
            }
            data['topMatchesText'] = topMatchesText;
        }

        // Event effects text
        if (parentEvent) {

            // Get match contracts
            const matchContracts = await getEventMatchContracts(parentEvent);
            if (matchContracts.length) {
                // Get top match contract
                const topMatchContract = getArrayMaxByProperty(matchContracts, 'bonus');
                const matchContractHolder = await getMatchContractTalent(topMatchContract);
                const matchContractType = matchContractHolder && matchContractHolder.objectName === 'superstar' ? 'singles' : 'tag';

                if (matchContractHolder) {
                    // Check if match contract used
                    let matchContractUsed = false;
                    let championshipUsedFor;
                    let matchContractText;
                    if (topMatchContract.awarded) {
                        for (const match of eventMatches) {
                            const matchChampionships = await getMatchChampionships(match);
                            if (matchChampionships.length && objectInArray(matchContractHolder, (await getMatchParticipants(match)))) {
                                matchContractUsed = true;
                                // Get championship that doesn't belong to match contract holder and is not vacant
                                for (const c of matchChampionships) {
                                    let holder = await getChampionshipHolder(c);
                                    if (!c.vacated && holder && holder !== matchContractHolder) {
                                        championshipUsedFor = c;
                                        break;
                                    }
                                }
                            }
                        }
                    }

                    if (matchContractUsed) {
                        // Match contract text if used
                        if (championshipUsedFor) {
                            const champion = await getChampionshipHolder(championshipUsedFor);
                            const championshipUsedForName = getChampionshipDisplayName(championshipUsedFor);
                            const facesWord = matchContractType === 'singles' ? 'faces' : 'face';
                            const championWord = matchContractType === 'singles' ? 'champion' : 'champions';
                            const titleWord = matchContractType === 'singles' ? 'title' : 'titles';
                            matchContractText = `<span class="font-bold">${matchContractHolder.name} ${facesWord} ` +
                                `${champion.name}</span> tonight, using ${matchContractHolder.possessivePronoun} ` +
                                `${topMatchContract.name} Contract for a shot at the ${championshipUsedForName}. Can ` +
                                `${matchContractHolder.name} defeat the ${championWord} and win the ${titleWord}, or ` +
                                `will ${champion.name} end ${matchContractHolder.name}'s dream?`;
                        }
                    }
                    else {
                        const hasWord = matchContractType === 'singles' ? 'has' : 'have';
                        if ((await getRosterMemberSuperstars(matchContractHolder)).some(y => !checkAvailability(y, true,
                            false, false))) {
                            // Unavailable text
                            matchContractText = `<span class="font-bold">${matchContractHolder.name} ${hasWord} a ` +
                                `${topMatchContract.name} Contract</span> to challenge for a ${matchContractType} title ` +
                                `at ${event.name}. Unfortunately, ${matchContractHolder.pronoun} won't be able to use it ` +
                                `tonight due to unavailability.`;
                        } else {
                            // Fans upset text if not used
                            const isWord = matchContractType === 'singles' ? 'is' : 'are';
                            matchContractText = `As you might remember, <span class="font-bold">${matchContractHolder.name} ` +
                                `${hasWord} a ${topMatchContract.name} Contract</span> to challenge for a ${matchContractType} ` +
                                `title at ${event.name}. However, ${matchContractHolder.pronoun} ${isWord} not booked to use it ` +
                                `tonight, and fans are quite upset about it.`;
                        }
                    }

                    if (matchContractText) {
                        data['matchContractText'] = matchContractText;
                    }
                }
            }

            // Get streaks if paid and 'A' event prestige
            if (globalUser.paid && event.prestige === 'A') {
                const streaks = await getEventStreaks(parentEvent);
                if (streaks.length) {
                    let streakUsed = false;
                    let streakText;

                    // Get top streak
                    const topStreak = await getArrayMaxByProperty(streaks, 'length');
                    const streakHolder = await getStreakTalent(topStreak);
                    const streakType = streakHolder && streakHolder.objectName === 'superstar' ? 'singles' : 'tag';
                    const streakAdjective = getStreakAdjective(topStreak.length);

                    // Check if streak used
                    let streakOpponents;
                    for (const match of eventMatches) {
                        const matchParticipants = await getMatchParticipants(match);
                        if (objectInArray(streakHolder, matchParticipants)) {
                            streakUsed = true;
                            streakOpponents = matchParticipants.filter(x => x.$loki !== streakHolder.$loki);
                        }
                    }

                    if (streakUsed) {
                        // Streak text if used
                        const defendWord = streakType === 'singles' ? 'defends' : 'defend';
                        const oneWord = streakType === 'singles' ? 'one' : 'ones';
                        let streakOpponentsText, streakOpponentName;
                        if (streakOpponents.length) {
                            streakOpponentsText = commaSeparate(streakOpponents.map(x => x.name));
                            streakOpponentName = getRandomChoice(streakOpponents).name;
                        } else {
                            streakOpponentsText = '???';
                            streakOpponentName = '???';
                        }

                        streakText = `<span class="font-bold">${streakHolder.name} ${defendWord} ` +
                            `${streakHolder.possessivePronoun} ${streakAdjective} ${topStreak.length}-0 streak at ` +
                            `${event.name} against ${streakOpponentsText}</span>. Will the streak continue to ` +
                            `${topStreak.length + 1}-0 or could ${streakOpponentName} be the ${oneWord} to end it tonight?`;
                    }
                    else {
                        const hasWord = streakType === 'singles' ? 'has' : 'have';
                        if ((await getRosterMemberSuperstars(streakHolder)).some(y => !checkAvailability(y, true,
                            false, false))) {
                            // Unavailable text
                            streakText = `<span class="font-bold">${streakHolder.name} ${hasWord} a ${streakAdjective} ` +
                                `${topStreak.length}-0 streak at ${event.name}</span>, but ${streakHolder.pronoun} ` +
                                `unfortunately won't be able to defend it due to unavailability.`;
                        }
                        else {
                            // Fans let down text if not used
                            const isWord = streakType === 'singles' ? 'is' : 'are';
                            streakText = `<span class="font-bold">${streakHolder.name} ${hasWord} a ${streakAdjective} ` +
                                `${topStreak.length}-0 streak at ${event.name}</span>, so it comes as a surprise that ` +
                                `${streakHolder.pronoun} ${isWord} not defending it tonight.`;
                        }
                    }

                    data['streakText'] = streakText;
                }
            }
        }

        // Get match predictions
        data['predictionsData'] = await getEventPreviewPredictions(eventMatches);
    }

    return data;
}

async function getEventPreviewMatchText(match, extraText = false, compactText = false) {  // get_event_preview_match_text
    const matchChampionships = await getMatchChampionships(match);
    const championshipsSuperstarIds = matchChampionships.map(c => c.superstarId);
    const participants = await getMatchParticipants(match);
    const matchGimmick = await getMatchGimmickMatch(match);

    // Sort participants by champions first, then by popularity
    let sortedParticipants = [...participants];
    for (const x of sortedParticipants) {
        x.tempPopularity = await getRosterMemberPopularity(x);
        x.tempChampion = (await getRosterMemberSuperstars(x)).some(z => championshipsSuperstarIds.includes(z.$loki));
    }
    sortedParticipants.sort((a, b) => b.tempChampion - a.tempChampion || b.tempPopularity - a.tempPopularity);
    for (const x of sortedParticipants) {
        delete x.tempPopularity;
        delete x.tempChampion;
    }

    // Add participants text
    let participantsText;
    if (participants.length <= 10) {
        const participantsNamesText = await Promise.all(sortedParticipants.map(x =>
            getEventPreviewParticipantLabel(x, championshipsSuperstarIds)));
        // Extra text if singles match without gimmick or title
        if (extraText && !compactText && !matchGimmick && !matchChampionships.length && participants.length === 2) {
            participantsText = participantsNamesText.join(' facing off against ');
        } else {
            participantsText = participantsNamesText.join(' vs ');
        }
    } else {
        // Get names of top participants only
        const topParticipants = sortedParticipants.slice(0, 6);
        const topParticipantsNamesText = await Promise.all(topParticipants.map(x =>
            getEventPreviewParticipantLabel(x, championshipsSuperstarIds)));
        const numberOfOthers = sortedParticipants.length - topParticipants.length;
        participantsText = `${topParticipantsNamesText.join(' vs ')} vs ${numberOfOthers} others`;
    }

    // Add championships text
    let championshipsText = '';
    if (matchChampionships.length) {
        const championshipsNames = commaSeparate(matchChampionships.map(x => getChampionshipDisplayName(x)));
        if (!compactText) {
            championshipsText = ` for the ${championshipsNames}`;
        } else {
            championshipsText = `${championshipsNames}: `;
        }
    }

    // Add gimmick match text
    let gimmickMatchText = '';
    if (matchGimmick) {
        if (!compactText) {
            const gimmickMatchPrefix = ['a', 'e', 'i', 'o', 'u'].includes(matchGimmick.name[0].toLowerCase()) ? 'an' : 'a';
            gimmickMatchText = ` in ${gimmickMatchPrefix} ${matchGimmick.name} Match`;
        } else {
            gimmickMatchText = ` (${matchGimmick.name} Match)`;
        }
    }

    let text;
    if (!compactText) {
        text = `<span class="font-bold">${participantsText}${championshipsText}</span>${gimmickMatchText}`;
    } else {
        text = `${championshipsText}${participantsText}${gimmickMatchText}`;
    }

    return text;
}

async function getEventPreviewPredictions(eventMatches) {  // get_event_preview_predictions
    const data = {};
    const predictions = [];
    const allMatches = [...eventMatches].sort((a, b) => a.order - b.order);
    const matchFinishTextChance = 15;
    const noContestChance = 4;
    const noInterestChance = 30;
    const noPickChance = 10;
    const percentagesChance = 6;
    const drawChance = 20;

    for (const match of allMatches) {
        const participants = await getMatchParticipants(match);
        const championships = await getMatchChampionships(match);
        const matchGimmick = await getMatchGimmickMatch(match);

        // Don't run for matches without participants e.g tournament finals
        if (!participants.length) {
            continue;
        }

        // Get predicted winner
        const weights = await getWinLossPercentagesWeights(participants, championships, 'standard', false);
        const winner = weightedChoice(weights);
        const firstRoundedPercentage = Math.ceil(weights[0][1] / 10) * 10;

        // No interest chance if hype is low
        let predictionText;
        if (match.hype < -20 && getRandomInt(0, 100) <= noInterestChance) {
            predictionText = getRandomChoice(['Who cares?', "Don't care.", 'No interest.', 'Not interested.']);
        }
        // No pick/draw chance if weights are close
        else if (participants.length === 2 && 55 >= weights[0][1] && weights[0][1] >= 45 &&
            getRandomInt(0, 100) <= noPickChance) {
            if (!matchGimmick && getRandomInt(0, 100) <= drawChance) {
                predictionText = 'Draw.';
            } else {
                predictionText = getRandomChoice(["Can't pick.", "Can't decide.", 'No idea.', 'No idea who wins.']);
            }
        }
        // Percentages chance if percentage is not 100/0 or 50/50
        else if (participants.length === 2 && ![100, 50].includes(firstRoundedPercentage) &&
            getRandomInt(0, 100) <= percentagesChance) {
            // Round percentages to 10s
            const secondRoundedPercentage = Math.floor(weights[1][1] / 10) * 10;
            predictionText = `${firstRoundedPercentage}% ${weights[0][0].name}, ${secondRoundedPercentage}% ${weights[1][0].name}.`;
        }
        // No contest chance if not high hype and not gimmick match
        else if (match.hype < 40 && !matchGimmick && getRandomInt(0, 100) <= noContestChance) {
            predictionText = 'No Contest';
        }
        else {
            // Get win/retain text
            const winWord = winner.objectName === 'superstar' ? 'wins' : 'win';
            const retainWord = winner.objectName === 'superstar' ? 'retains' : 'retain';
            let predictionWord;
            if (championships.length === 1) {
                const championshipSuperstarIds = championships.map(x => x.superstarId);
                const winnerSuperstarIds = (await getRosterMemberSuperstars(winner)).map(x => x.$loki);
                // Check if any winner superstar holds match championship
                if (winnerSuperstarIds.some(x => championshipSuperstarIds.includes(x))) {
                    predictionWord = retainWord;
                } else {
                    if (championships.some(x => x.matchContract || x.championshipContract)) {
                        const titleWord = winner.objectName === 'superstar' ? 'contract' : 'contracts';
                        predictionWord = `${winWord} the ${titleWord}`;
                    } else {
                        const titleWord = winner.objectName === 'superstar' ? 'title' : 'titles';
                        predictionWord = `${winWord} the ${titleWord}`;
                    }
                }
            } else {
                predictionWord = winWord;
            }

            // Match finish text chance if title didn't change hands and not gimmick match and not face win
            if ((!championships.length || (championships.length === 1 && predictionWord === retainWord)) &&
                (await getRosterMemberAlignment(winner)) !== 'face' && !matchGimmick &&
                getRandomInt(0, 100) <= matchFinishTextChance) {
                const matchFinishes = matchFinishesData.filter(x => x.category === 'dusty');
                if (matchFinishes.length) {
                    const matchFinish = getRandomChoice(matchFinishes);
                    if (matchFinish.name) {
                        predictionWord += ` via ${matchFinish.name}`;
                    }
                }
            }

            predictionText = `${winner.name} ${predictionWord}.`;
        }

        predictions.push({'matchText': await getEventPreviewMatchText(match, false, true),
            'predictionText': predictionText});
    }

    // Get journalist data
    const journalistData = generateJournalistData();
    data['journalistName'] = journalistData.name;
    data['journalistPossessivePronoun'] = journalistData.possessivePronoun;

    data['predictions'] = predictions;

    return data;
}

function generateJournalistData() {  // generate_journalist_data
    const data = {};
    const gender = getRandomChoice(['male', 'female']);
    const name = generateName(gender);
    const possessivePronoun = gender === 'male' ? 'his' : 'her';
    data['name'] = name;
    data['possessivePronoun'] = possessivePronoun;
    return data;
}

async function getEventPreviewParticipantLabel(participant, championshipsSuperstarIds) {  // get_event_preview_participant_label
    // Add champion label if champion
    const superstars = await getRosterMemberSuperstars(participant);
    if (superstars.some(x => championshipsSuperstarIds.includes(x.$loki))) {
        return `${participant.name} (c)`;
    } else {
        return participant.name;
    }
}

function getStreakAdjective(length) {  // get_streak_adjective
    // Get adjective for streak length for event preview
    if (length < 5) {
        return 'budding';
    } else if (length < 10) {
        return 'remarkable';
    } else if (length < 20) {
        return 'fantastic';
    } else {
        return 'legendary';
    }
}

export {getNextMonthlyEvent, getMonthlyEventForWeeklyEvent, getUpcomingMonthlyEventForWeeklyEvent,
    getLatestMonthlyEvent, getWeeklyEventName, getEventMaximumMatchesAndSegments, getEventMinimumMatchesAndSegments,
    getEventMatchesData, getEventSegmentsData, getEventBaseHype, getTopEvent, getEventTitle, getDatetimeForEvent,
    checkEventWithinDatetime, getParentEventForEvent, getEventPosterData, getViewEventsData, getCompanyEventsData,
    rebrandEvent, getEventCardData, getStreakData, getEventMatchesAndSegments, playEvent, getSuperstarsUsedAtEvent,
    getEventHype, getNextEvent, calculateEventRating, getEventPosterGraphicData, endEventRequest, getEventPreviewMatchText,
    generateJournalistData, getNielsenRatingFromViewership, addWeeklyShow}